'use client';

import React, { useEffect, useMemo, useState } from 'react';
import { Dropdown } from '@/components/ui/Dropdown';
import type { DropdownOption } from '@/components/ui/Dropdown';
import { BottomSheet, Button } from '@/components/ui';
import { DropdownField } from '@/components/ui/DropdownField';
import { Picker } from '@/components/picker';
import { cn } from '@/lib/cn';
import { createPriceRangeOptions } from '@repo/utils';
import { IconAlertCircleFilled } from '@repo/svgs';
import { useIsMobile } from '@/hooks/useDeviceType';

const BEDROOM_VALUES = [0, 1, 2, 3, 4] as const;

function bedroomLabel(value: number) {
  if (value === 0) return 'Studio';
  if (value === 4) return '4+';
  return String(value);
}

export interface ListingAlertPriceAndBedroomsProps {
  maxRent: number | null;
  numBedrooms: number[] | null;
  isLoading?: boolean;
  isSaving?: boolean;
  showMaxPriceError?: boolean;
  showBedroomsError?: boolean;
  onMaxRentChange: (value: number | null) => void;
  onNumBedroomsChange: (value: number[] | null) => void;
  className?: string;
  /**
   * When true, forces max-price + bedrooms to stack vertically on ALL
   * viewport sizes (dropping the `xl:flex-row` default that makes them
   * sit inline on wide screens). Used by the Renter Profile wizard,
   * which lives inside a narrower modal on desktop where the inline
   * layout crams the max-price dropdown into a tiny "$" pill next to
   * the bedroom chips. Also switches labels to the Figma-spec form
   * ("Max price *" / "Bedrooms *") for the wizard's required fields.
   */
  stacked?: boolean;
  /**
   * Wizard-only field-tap analytics hooks. Fired on tap-capture over
   * each control so the wizard can attribute engagement to
   * `preferences_max_price` / `preferences_bedrooms` without having
   * to instrument every internal button here. Explicit `| undefined`
   * lets forwarding wrappers pass a literal `undefined` under
   * `exactOptionalPropertyTypes`.
   */
  onMaxPriceTap?: (() => void) | undefined;
  onBedroomsTap?: (() => void) | undefined;
}

/**
 * Standalone saved-page controls for alert max price + bedrooms.
 * Uses the listing alert API state, not listings filter store state.
 */
export function ListingAlertPriceAndBedrooms({
  maxRent,
  numBedrooms,
  isLoading = false,
  isSaving = false,
  showMaxPriceError = false,
  showBedroomsError = false,
  onMaxRentChange,
  onNumBedroomsChange,
  className,
  stacked = false,
  onMaxPriceTap,
  onBedroomsTap,
}: ListingAlertPriceAndBedroomsProps) {
  const disabled = isLoading || isSaving;
  const isMobile = useIsMobile();
  const selectedBedrooms = numBedrooms ?? [];
  const [isMaxPriceSheetOpen, setIsMaxPriceSheetOpen] = useState(false);
  const [draftMaxRent, setDraftMaxRent] = useState<number | null>(maxRent);

  const maxPriceOptions = useMemo<DropdownOption<number>[]>(
    () =>
      createPriceRangeOptions().map(option => ({
        value: option.value,
        label: option.label,
      })),
    [],
  );

  const mobileMaxPriceOptions = useMemo(
    () =>
      maxPriceOptions.map(option => ({
        value: option.value,
        label: String(option.label),
      })),
    [maxPriceOptions],
  );

  useEffect(() => {
    if (!isMaxPriceSheetOpen) {
      setDraftMaxRent(maxRent);
    }
  }, [isMaxPriceSheetOpen, maxRent]);

  return (
    <div
      className={cn(
        // Default (compact /saved card): fields stack on narrow
        // screens, sit inline on xl+ so the card doesn't waste
        // vertical space. `stacked` (wizard) forces column layout
        // everywhere — the wizard modal is narrow even on desktop.
        'mt-4 flex flex-col gap-4',
        !stacked && 'xl:flex-row xl:items-start xl:justify-between xl:gap-8',
        stacked && 'gap-6',
        className,
      )}
    >
      <div
        className={cn('min-w-0', !stacked && 'xl:flex-1')}
        // Fire only when the tap lands on the max-price control
        // itself — the dropdown trigger, its portal buttons, or
        // the underlying input. Taps on the "Max price" label span
        // above or empty padding are ignored.
        onClick={e => {
          if (
            onMaxPriceTap &&
            (e.target as HTMLElement).closest('button, input, [role="combobox"]')
          ) {
            onMaxPriceTap();
          }
        }}
      >
        <div className="text-body-2-high text-secondary pb-1">Max price{stacked ? ' *' : ''}</div>
        {isMobile ? (
          <>
            <DropdownField
              placeholder="Select"
              selectedLabel={
                maxPriceOptions.find(option => option.value === maxRent)?.label ?? undefined
              }
              disabled={disabled}
              alert={showMaxPriceError}
              helperText={showMaxPriceError ? 'Please select max price.' : undefined}
              isOpen={isMaxPriceSheetOpen}
              className="!h-11.5"
              onClick={() => {
                if (disabled) return;
                setDraftMaxRent(maxRent);
                setIsMaxPriceSheetOpen(true);
              }}
            />
            <BottomSheet
              label="Select max price"
              open={isMaxPriceSheetOpen}
              onClose={() => setIsMaxPriceSheetOpen(false)}
              leading={false}
              bodyClassName="py-8 px-6"
              footer={
                <div className="flex items-center gap-4 bg-primary px-4 py-4">
                  <Button
                    variant="tertiary"
                    className="flex-1"
                    disabled={draftMaxRent === null}
                    onClick={() => {
                      onMaxRentChange(null);
                      setIsMaxPriceSheetOpen(false);
                    }}
                  >
                    Reset
                  </Button>
                  <Button
                    className="flex-1"
                    onClick={() => {
                      onMaxRentChange(draftMaxRent);
                      setIsMaxPriceSheetOpen(false);
                    }}
                  >
                    Done
                  </Button>
                </div>
              }
            >
              <div className="flex justify-center">
                <Picker
                  name="maxPrice"
                  className="w-full max-w-[180px]"
                  options={mobileMaxPriceOptions}
                  value={draftMaxRent === null ? 'null' : draftMaxRent}
                  onChange={value => {
                    setDraftMaxRent(value === 'null' ? null : Number(value));
                  }}
                  formatLabel={option => option.label ?? ''}
                />
              </div>
            </BottomSheet>
          </>
        ) : (
          <Dropdown<number>
            value={maxRent}
            options={maxPriceOptions}
            placeholder="Select"
            disabled={disabled}
            alert={showMaxPriceError}
            helperText={showMaxPriceError ? 'Please select max price.' : undefined}
            className="!h-11.5"
            matchTriggerWidth
            menuClassName="w-full"
            onChange={value => onMaxRentChange(value)}
          />
        )}
      </div>

      <div
        className="min-w-0"
        onClick={e => {
          if (onBedroomsTap && (e.target as HTMLElement).closest('button')) {
            onBedroomsTap();
          }
        }}
      >
        <div className="text-body-2-high text-secondary pb-1">Bedrooms{stacked ? ' *' : ''}</div>
        <div
          className={cn(
            // Compact card wants chips inline on xl+; wizard wants
            // them always in the same grid (chips fill row width so
            // Studio + 1..4+ each take equal share).
            'grid grid-cols-[1.35fr_repeat(4,minmax(0,1fr))] gap-2',
            !stacked && 'xl:flex xl:flex-nowrap',
          )}
          role="group"
          aria-label="Bedrooms"
          aria-invalid={showBedroomsError || undefined}
        >
          {BEDROOM_VALUES.map(value => {
            const selected = selectedBedrooms.includes(value);
            return (
              <button
                key={value}
                type="button"
                onClick={() => {
                  if (disabled) return;
                  const next = selected
                    ? selectedBedrooms.filter(v => v !== value)
                    : [...selectedBedrooms, value].sort((a, b) => a - b);
                  onNumBedroomsChange(next.length > 0 ? next : null);
                }}
                aria-disabled={disabled}
                aria-pressed={selected}
                className={cn(
                  'inline-flex w-full min-w-0 items-center justify-center rounded-full border bg-primary text-body-2 text-primary transition-colors',
                  'px-2 py-3',
                  // Compact mode lets chips widen on xl+; wizard keeps
                  // them within the grid so all five sit on one row.
                  !stacked && 'xl:w-auto xl:px-4 xl:!min-w-12',
                  selected
                    ? '!bg-active hover:!bg-active-hover ring-accent ring-inset ring-1 border-accent'
                    : 'border-secondary hover:bg-primary-hover',
                  disabled && 'cursor-not-allowed opacity-60',
                )}
              >
                {bedroomLabel(value)}
              </button>
            );
          })}
        </div>
        {showBedroomsError ? (
          <div className="mt-2 flex items-center gap-1">
            <IconAlertCircleFilled className="h-4 w-4 text-alert shrink-0" />
            <span className="text-caption text-alert">Please select bedrooms.</span>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default ListingAlertPriceAndBedrooms;
